// A2/A4/A5/E1–E3 — chuỗi hiển thị của bong bóng tin, danh sách dự án: logic thuần để Main dùng và test E2E import trực tiếp
// (Node chạy được file .ts nhờ type stripping → chỉ dùng cú pháp TS xóa được).
import { fileLabel, locationLabel, readStatusOf, type LocalMessage, type ReceiptState } from '@swchat/client-sdk';
import type { Project } from '@swchat/protocol';

/** Ký tự thuộc tên — trùng `NAME_CHAR_RE` của `detectMentions` trong SDK để tô sáng khớp người thực sự được nhắc. */
const NAME_CHAR = '[\\p{L}\\p{N}\\p{M}_]';

export interface TextSegment {
  kind: 'text' | 'tag' | 'mention' | 'link';
  text: string;
}

function escapeRe(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Tách văn bản thành đoạn thường, tag dự án `#CODE` và mention `@Tên` (tên dài nhất thắng, có ranh giới hai phía
 * như `detectMentions`: `@Bình` trong `@BìnhAn` hay `x@Bình` không tô sáng).
 */
export function splitRichText(text: string, names: readonly string[]): TextSegment[] {
  const valid = names.filter(Boolean);
  const mention = valid.length
    ? `|(?<!${NAME_CHAR})@(?:${[...valid].sort((a, b) => b.length - a.length).map(escapeRe).join('|')})(?!${NAME_CHAR})`
    : '';
  // Liên kết đứng đầu để `#fragment` hay `@` bên trong URL không bị tô như tag/mention.
  const re = new RegExp(`(${URL_RE_SRC}|#[A-Z0-9][A-Z0-9_-]{1,31}${mention})`, 'gu');
  const out: TextSegment[] = [];
  text.split(re).forEach((part, i) => {
    if (!part) return;
    out.push({ kind: i % 2 === 0 ? 'text' : part.startsWith('#') ? 'tag' : part.startsWith('@') ? 'mention' : 'link', text: part });
  });
  return out;
}

/** Bỏ dấu câu cuối (`.,;:!?` và ngoặc/nháy đóng) khỏi địa chỉ. */
const URL_TAIL = '[^\\s<>]*[^\\s<>.,;:!?\'")\\]}]';

/**
 * Đuôi tên miền được nhận khi không có `http(s)://` / `www.` — cố ý ngắn gọn để `index.js`, `Node.js`, `v1.2` không bị
 * gắn link nhầm. Viết cả hoa lẫn thường bằng lớp ký tự (regex chính phân biệt hoa/thường vì tag `#MÃ` chỉ viết hoa).
 */
const BARE_TLDS = ['com', 'net', 'org', 'io', 'dev', 'app', 'vn', 'edu', 'gov', 'info', 'biz', 'co', 'me', 'xyz', 'ai', 'tech', 'cloud'];
const anyCase = (s: string) => [...s].map((c) => `[${c}${c.toUpperCase()}]`).join('');

/**
 * Địa chỉ web trong chữ thường: `http(s)://…`, `www.…`, hoặc tên miền trần `ten.mien.com[:cổng][/đường-dẫn]`
 * (không dính vào email `a@b.com`, không nằm giữa từ/đường dẫn khác).
 */
const URL_RE_SRC =
  `(?:https?:\\/\\/|www\\.)${URL_TAIL}` +
  `|(?<![\\p{L}\\p{N}\\p{M}_@./-])(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+(?:${BARE_TLDS.map(anyCase).join('|')})(?![\\p{L}\\p{N}\\p{M}_-])(?::\\d{1,5})?(?:[/?#]${URL_TAIL})?`;

/** `href` an toàn cho một đoạn `link` (thêm `https://` khi chưa có scheme). */
export function linkHref(text: string): string {
  return /^https?:\/\//i.test(text) ? text : `https://${text}`;
}

/** "Đã xem"/"Đã nhận" (1-1) hoặc "n người đã xem"/"n người đã nhận" (nhóm) cho tin của mình. */
export function receiptLabel(
  m: LocalMessage,
  receipts: Record<string, ReceiptState>,
  me: string | undefined,
  direct: boolean,
): string | undefined {
  const status = readStatusOf(m, receipts, me);
  if (direct) return status.read.length ? 'Đã xem' : status.delivered.length ? 'Đã nhận' : undefined;
  if (status.read.length) return `${status.read.length} người đã xem`;
  return status.delivered.length ? `${status.delivered.length} người đã nhận` : undefined;
}

/** Dòng "Đã chuyển tiếp từ …" của tin chuyển tiếp, hoặc `undefined` khi không phải tin chuyển tiếp / đã xóa. */
export function forwardedLabel(m: LocalMessage, senderName: (userId: string) => string | undefined): string | undefined {
  if (!m.forwarded_from || m.deleted) return undefined;
  return `↪ Đã chuyển tiếp từ ${senderName(m.forwarded_from.sender) ?? 'người khác'}`;
}

/** Nội dung khung trích dẫn của tin trả lời: tin gốc (người gửi + nội dung) hoặc báo không có trên thiết bị. */
export function quoteText(
  quoted: LocalMessage | null,
  senderName: (userId: string) => string | undefined,
): { sender?: string; text: string } {
  if (!quoted) return { text: 'Tin gốc không có trên thiết bị này' };
  const text = quoted.deleted ? 'Tin nhắn đã bị xóa' : quoted.type === 'file' ? fileLabel(quoted) : quoted.type === 'poll' ? `📊 ${quoted.text ?? ''}` : quoted.type === 'location' ? locationLabel(quoted) : (quoted.text ?? '');
  return { sender: senderName(quoted.sender_user_id) ?? '…', text };
}

/** E1: nhãn dự án trong ô chọn (lọc danh sách hội thoại, gắn dự án khi tạo nhóm). */
export function projectLabel(p: Pick<Project, 'code' | 'name'>): string {
  return `#${p.code} · ${p.name}`;
}

/** E1: tùy chọn ô chọn dự án — mục đầu `''` (tất cả / không gắn dự án), sau đó từng dự án theo nhãn `#MÃ · Tên`. */
export function projectOptions(projects: readonly Pick<Project, 'id' | 'code' | 'name'>[], emptyLabel: string) {
  return [{ value: '', label: emptyLabel }, ...projects.map((p) => ({ value: p.id, label: projectLabel(p) }))];
}

/** E1: ô lọc theo dự án trên danh sách hội thoại chỉ hiện khi có dự án và không đang tìm kiếm tin. */
export function showProjectFilter(projects: readonly unknown[], query: string): boolean {
  return projects.length > 0 && !query.trim();
}

/** E2: lọc danh sách hội thoại theo dự án (`''` = tất cả), giữ nguyên thứ tự. */
export function filterByProject<T extends { project_id?: string | null }>(conversations: readonly T[], projectId: string): T[] {
  return projectId ? conversations.filter((c) => c.project_id === projectId) : [...conversations];
}

/** C2: kích thước file dễ đọc (`0 B`, `512 B`, `1,5 KB`, `3,5 MB`) — đơn vị 1024, dấu phẩy thập phân kiểu Việt. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 1024) return `${Math.max(0, Math.round(bytes || 0))} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const rounded = value >= 100 ? Math.round(value).toString() : (Math.round(value * 10) / 10).toString();
  return `${rounded.replace('.', ',')} ${units[unit]}`;
}

/** C2: tin file hiển thị như ảnh inline (tự tải → giải mã → object URL) hay thẻ file (tên + kích thước + nút tải). */
export function isInlineImage(file: { mime: string; size: number }): boolean {
  // Ảnh lớn không tự tải (tốn băng thông/bộ nhớ) — hiện như file thường, người dùng tự bấm tải.
  return /^image\/(png|jpe?g|gif|webp|bmp|avif)$/i.test(file.mime) && file.size <= 20 * 1024 * 1024;
}
